// Live "today's bookings" counter — Apple/Tesla-style social proof on landing page
import { useEffect, useState } from "react";
import { TrendingUp, MapPin, Leaf } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LangContext";
import { useCountUp } from "../hooks/useCountUp";

function StatItem({ icon: Icon, value, label, format }) {
  const numericValue = typeof value === "number" && !Number.isNaN(value) ? value : 0;
  const display = useCountUp(numericValue, { start: true, duration: 900 });
  return (
    <div className="flex items-center gap-3 sm:gap-4">
      <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#FF5A00]/10 flex items-center justify-center">
        <Icon size={18} className="text-[#FF5A00]" />
      </div>
      <div>
        <div className="text-2xl sm:text-3xl font-black tracking-tighter tabular-nums leading-none">
          {format ? format(Number(String(display).replace(/\D/g, "")) || 0) : display}
        </div>
        <div className="text-[10px] sm:text-[11px] font-bold tracking-[0.18em] uppercase text-neutral-500 mt-1">
          {label}
        </div>
      </div>
    </div>
  );
}

export default function TodayCounter() {
  const { t, lang } = useLang();
  const ct = t.today_counter;
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await api.get("/stats/public-counter");
        if (mounted) setStats(r.data);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[TodayCounter] stats fetch failed:", err);
        }
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  if (!stats) return null;

  const formatNum = (n) => new Intl.NumberFormat(lang === "ar" ? "ar-DE" : "de-DE").format(n);

  return (
    <section
      className="relative py-8 sm:py-10 border-y border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#0A0B0D] overflow-hidden"
      data-testid="today-counter-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-neutral-700 dark:text-neutral-300">
              {ct.title}
            </p>
          </div>
          <div className="flex flex-wrap gap-6 sm:gap-10">
            <StatItem icon={TrendingUp} value={stats.bookings_today} label={ct.today_label} />
            <StatItem icon={MapPin} value={stats.km_driven} label={ct.km_label} format={formatNum} />
            <StatItem icon={Leaf} value={Math.round(stats.co2_saved_kg)} label={ct.co2_label} format={formatNum} />
          </div>
        </div>
      </div>
    </section>
  );
}
